import { CloseIcon } from "@netapp/bxp-style/react-icons/Action";
import { DropdownArrowDownIcon, DropdownArrowUpIcon } from "@netapp/bxp-style/react-icons/Navigation";
import classNames from "classnames";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import uuidv4 from "uuid/v4";
import { ThreeDotLoader } from "../../index";
import { KEYBOARD_KEYS } from "../../utils";
import { useSelect } from "../../hooks/useSelect";
import Menu from "./Menu";
import { MultiSelectedOptionsRenderer, SelectedOptionRenderer, SummaryOptionRenderer } from "./OptionsRenderers";
import styles from "./Select.module.scss";

const Select = React.memo(({
    placeholder,
    value,
    isLoading,
    showSecondaryLabelInSelectedOption,
    isDisabled,
    options,
    isClearable,
    isSummaryView = false,
    isOpened = false,
    onOpenOrClose = _.noop,
    onChange,
    name,
    hasError,
    menuPortalTarget,
    isSearchable,
    isMulti,
    noOptionsMessage,
    isRequireAcceptOnChange,
    isCreatable,
    dataTestId,
    hasSelectAll = false,
    isReadOnly = false
}) => {
    const componentId = useRef(uuidv4());
    const [isMenuOpen, setMenuOpen] = useState(isOpened);
    const controlRef = useRef(null);
    const { selectionState, internalSelectionState, toggleOption, unselectOption, clearAll, acceptChanges, createOption, optionsState, toggleSelectAll } = useSelect(componentId, isRequireAcceptOnChange, value, isMulti, onChange, name, isMenuOpen, options);

    useEffect(() => {
        onOpenOrClose(isMenuOpen);
    },[isMenuOpen]);

    const handleKeyDown = (isDisabled || isLoading || isReadOnly) ? undefined : e => {
        if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.BACKSPACE) {
            setMenuOpen(prev => !prev);
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const onClick = useCallback((e) => {
        if (e.__multiSelectClose === componentId.current || e?.nativeEvent?.__multiSelectClose === componentId.current ||
            e.__multiSelectRemove || e?.nativeEvent.__multiSelectRemove ||
            e.__multiSelectClear || e?.nativeEvent.__multiSelectClear
        ) {
            return;
        }

        setMenuOpen(prev => !prev);
        e.stopPropagation();
        e.preventDefault();
    }, [setMenuOpen]);

    return <>
        <div data-testid={dataTestId} className={classNames(styles["control"], { [styles["invalid"]]: hasError, [styles["disabled"]]: isDisabled, [styles["read-only"]]: isReadOnly, [styles["open"]]: isMenuOpen })} onClick={(isDisabled || isLoading || isReadOnly) ? _.noop : onClick} ref={controlRef}
            tabIndex={isDisabled ? -1 : 0}
            onKeyDown={handleKeyDown}>
            {!selectionState && <div className={styles["placeholder"]}>{placeholder || "Select..."}</div>}
            {isLoading && <ThreeDotLoader className={styles["loading"]} />}
            {selectionState && !isMulti && <SelectedOptionRenderer
                width={controlRef.current?.offsetWidth}
                selectionState={selectionState}
                isSelectDisabled={isDisabled}
                showSecondaryLabelInSelectedOption={showSecondaryLabelInSelectedOption} />}
            {selectionState && isSummaryView && isMulti && <SummaryOptionRenderer selectionState={selectionState} />}
            {selectionState && !isSummaryView && isMulti && <MultiSelectedOptionsRenderer
                unselectOption={unselectOption}
                toggleOption={toggleOption}
                name={name}
                selectionState={selectionState}
                showSecondaryLabelInSelectedOption={showSecondaryLabelInSelectedOption}
                isRequireAcceptOnChange={isRequireAcceptOnChange}
                isSelectDisabled={isDisabled}
            />}
            {!isLoading && <div className={styles["controls"]}>
                {isClearable && !isReadOnly && !isDisabled && selectionState && <button
                    style={{ cursor: isDisabled ? "default" : "pointer" }}
                    disabled={isDisabled}
                    onClick={clearAll}>
                    <CloseIcon color={isDisabled ? "disabled" : "text"} />
                </button>}
                {isMenuOpen ? <DropdownArrowUpIcon color={isDisabled ? "disabled" : "text"} /> : <DropdownArrowDownIcon color={isDisabled ? "disabled" : "primary"} />}
            </div>}
        </div>
        {isMenuOpen && <Menu noOptionsMessage={noOptionsMessage}
            options={optionsState}
            name={name}
            unselectOption={unselectOption}
            toggleOption={toggleOption}
            selectionState={isRequireAcceptOnChange ? internalSelectionState : selectionState}
            controlRef={controlRef}
            setMenuOpen={setMenuOpen}
            menuPortalTarget={menuPortalTarget}
            isSearchable={isSearchable}
            isCreatable={isCreatable}
            isMulti={isMulti}
            componentId={componentId}
            isRequireAcceptOnChange={isRequireAcceptOnChange}
            acceptChanges={acceptChanges}
            createOption={createOption}
            hasSelectAll={hasSelectAll}
            toggleSelectAll={toggleSelectAll}
        />}
    </>
})

export default Select;